import { createAsyncThunk } from '@reduxjs/toolkit';
import { getApi, postApi } from 'redux/apis';

export const importExpenseGroupAllocationCsvAction = createAsyncThunk(
	'/configure-allocations/allocations/importExpenseConfigAllocation',
	async (
		params: {
			expenseGroupId: string;
			accounts: string[];
			rows: Array<{
				customer?: string;
				class?: string;
				location?: string;
				percentage?: number;
			}>;
		},
		{ rejectWithValue }
	) => {
		try {
			const companyId = localStorage.getItem('companyId');
			const response = await postApi(
				'/configure-allocations/allocations/importExpenseConfigAllocation',
				{
					companyId,
					expenseGroupId: params.expenseGroupId,
					accounts: params.accounts,
					rows: params.rows,
				}
			);
			return response.data?.data ?? {};
		} catch (error: any) {
			return rejectWithValue(error?.response?.data);
		}
	}
);

export const fetchExpenseGroupAllocationsAction = createAsyncThunk(
	'expense-group-allocation/list',
	async (params: { expenseGroupId: string }, { rejectWithValue }) => {
		try {
			const companyId = localStorage.getItem('companyId');
			const response = await getApi('/configure-allocations/allocations', {
				companyId,
				expenseGroupId: params.expenseGroupId,
			});
			// Return the `data` object which contains { configAccounts, allocations }
			return response.data?.data ?? { allocations: [], configAccounts: [] };
		} catch (error: any) {
			return rejectWithValue(error?.response?.data);
		}
	}
);

export const upsertExpenseGroupAllocationsBatchAction = createAsyncThunk(
	'expense-group-allocation/upsert-batch',
	async (
		params: {
			expenseGroupId: string;
			configAccounts?: string[];
			accounts?: string[];
			items: Array<{
				percentage: number;
				customerId?: string;
				customerName?: string;
				classId?: string;
				className?: string;
				locationId?: string;
				locationName?: string;
			}>;
			isSingleAccountAllocation?: boolean;
		},
		{ rejectWithValue }
	) => {
		try {
			const companyId = localStorage.getItem('companyId');
			const isSingleAccountAllocation = params.isSingleAccountAllocation ?? false;

			// Build payload based on API requirements
			const payload: any = {
				companyId,
				expenseGroupId: params.expenseGroupId,
				items: params.items,
				isSingleAccountAllocation,
			};

			if (isSingleAccountAllocation) {
				// When switch is ON: send configAccounts (required, non-empty)
				// Don't send accounts field
				if (!params.configAccounts || params.configAccounts.length === 0) {
					return rejectWithValue({
						message: 'configAccounts is required and must be non-empty when isSingleAccountAllocation is true',
					});
				}
				payload.configAccounts = params.configAccounts;
			} else {
				// When switch is OFF: send accounts (required, can be empty array)
				// Don't send configAccounts field
				payload.accounts = params.accounts || [];
			}

			const response = await postApi(
				'/configure-allocations/allocations/upsert-batch',
				payload
			);
			return response.data?.data ?? {};
		} catch (error: any) {
			return rejectWithValue(error?.response?.data);
		}
	}
);

// Get Expense Allocation Data
export const getExpenseAllocationAction = createAsyncThunk(
	'expense-group-allocation/getAllocation',
	async (
		params: {
			expenseGroupId: string;
			fromDate: string;
			toDate: string;
			page?: number;
			limit?: number;
			// Required: tempFlag determines data source
			tempFlag: boolean;
			// Conditional: required when tempFlag = true
			tempAccounts?: string[];
			tempConfigAllocations?: Array<{
				percentage: number;
				customerId?: string | null;
				customerName?: string | null;
				classId?: string | null;
				className?: string | null;
				locationId?: string | null;
				locationName?: string | null;
			}>;
		},
		{ rejectWithValue }
	) => {
		try {
			const companyId = localStorage.getItem('companyId');

			// Base payload - always required
			const payload: any = {
				companyId,
				expenseGroupId: params.expenseGroupId,
				fromDate: params.fromDate,
				toDate: params.toDate,
				tempFlag: params.tempFlag,
				page: params.page || 1,
				limit: params.limit || 20,
			};

			// Add temporary mode fields when tempFlag = true
			if (params.tempFlag === true) {
				payload.tempAccounts = params.tempAccounts || [];
				payload.tempConfigAllocations = params.tempConfigAllocations || [];
			}
			// When tempFlag = false, no additional fields needed (backend uses database)

			const response = await postApi('/expense-journal/expense-allocation', payload);
			return response.data;
		} catch (error: any) {
			return rejectWithValue(error?.response?.data);
		}
	}
);
